import { getAccessToken } from '../authentication/supabaseService';

const API_BASE = '/api/admin';

// ── Types (mirror server/database/inventoryAiUsageDbService.ts) ──

export type AiUsageSource =
  | 'voice_agent'
  | 'voice_match_utterance'
  | 'voice_transcribe'
  | 'import_match';

export interface UsageTotals {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  audioSeconds: number;
  /** USD — Anthropic/OpenAI list prices are quoted in USD. */
  costUsd: number;
  lastAt: string;
}

export interface UsageBreakdown {
  total: UsageTotals;
  bySource: Partial<Record<AiUsageSource, UsageTotals>>;
}

export interface SessionUsage extends UsageBreakdown {
  restaurantId: string;
}

/** Unfiltered response: global total + all three rollups. */
export interface InventoryAiUsageResponse {
  month: string;
  total: UsageTotals;
  bySource: Partial<Record<AiUsageSource, UsageTotals>>;
  byRestaurant: Record<string, UsageBreakdown>;
  bySession: Record<string, SessionUsage>;
}

/** The bucket the server books spend to when it can't resolve a restaurant (e.g. /transcribe). */
export const UNATTRIBUTED_RESTAURANT = 'unattributed';

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
  }

  return response.json();
}

/** Inventory AI usage + cost for one month (`YYYY-MM`). */
export async function getInventoryAiUsage(month: string): Promise<InventoryAiUsageResponse> {
  return fetchWithAuth(`/inventory-ai-usage?month=${encodeURIComponent(month)}`);
}
